import { useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Paper,
  FormControlLabel,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import type { Entity, NewEntity, LogoKind } from "../../api/types";
import { EntityFormDialog } from "./EntityFormDialog";
import { ENTITY_LABELS } from "../EntityPickerField/config";
import { logoUrl, useResetLogo, useUploadLogo } from "../../api/logo";

interface UpdateEntity<Id, Name> extends NewEntity<Name> {
  id: Id;
}

interface EntityListProps<Id> {
  title: string;
  entityType: LogoKind;
  data: Entity<Id, string>[];
  // onAdd must resolve to the created entity so the post-create logo upload
  // can address it by id. The mutation hooks (useAddSource, useAddGroup, …)
  // already return this; createEntityListPage forwards it.
  onAdd: (entity: NewEntity<string>) => Promise<Entity<Id, string>>;
  onUpdate: (entity: UpdateEntity<Id, string>) => Promise<unknown>;
  isLoading: boolean;
}

interface ErrorData {
  error?: string;
}

export const EntityList = <Id extends string>({
  title,
  entityType,
  data,
  onAdd,
  onUpdate,
}: EntityListProps<Id>) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity<
    Id,
    string
  > | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [showHidden, setShowHidden] = useState(false);
  const uploadLogo = useUploadLogo();
  const resetLogo = useResetLogo();

  const handleAdd = () => {
    setSelectedEntity(null);
    setErrorMessage(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = (entity: Entity<Id, string>) => {
    setSelectedEntity(entity);
    setErrorMessage(undefined);
    setIsDialogOpen(true);
  };

  const handleSave = async (
    entityData: NewEntity<string>,
    logoFile?: Blob | null,
    resetLogoFlag?: boolean,
  ) => {
    setErrorMessage(undefined);
    try {
      if (selectedEntity) {
        // Handle logo operations first so that the subsequent entity update
        // (which updates the 'updated' timestamp) allows the frontend to
        // fetch the new logo with a new version query param.
        if (logoFile) {
          await uploadLogo.mutateAsync({
            kind: entityType,
            id: selectedEntity.id,
            file: logoFile,
          });
        } else if (resetLogoFlag) {
          await resetLogo.mutateAsync({
            kind: entityType,
            id: selectedEntity.id,
          });
        }

        // Construct UpdateEntity (excluding created/updated)
        const updatedEntity: UpdateEntity<Id, string> = {
          id: selectedEntity.id,
          ...entityData,
        };
        await onUpdate(updatedEntity);
      } else {
        const created = await onAdd(entityData);
        if (logoFile) {
          try {
            await uploadLogo.mutateAsync({
              kind: entityType,
              id: created.id,
              file: logoFile,
            });
          } catch (logoErr) {
            // Entity create succeeded but the logo upload failed. Don't roll
            // back — the user has a usable entity. Switch the dialog into
            // edit mode for `created` so a retry on Save goes through the
            // edit path (PUT) instead of trying to add a duplicate name.
            console.error("Logo upload failed after entity creation:", logoErr);
            setSelectedEntity(created);
            setErrorMessage(
              `${title} was created, but the logo upload failed. Re-select the logo and Save to retry.`,
            );
            return;
          }
        }
      }
      setIsDialogOpen(false);
    } catch (error: unknown) {
      console.error("Failed to save entity:", error);
      let msg = "An unexpected error occurred.";
      const err = error as {
        data?: ErrorData;
        message?: string;
      };

      if (err.data && err.data.error) {
        msg = err.data.error;
      } else if (err.message) {
        msg = err.message;
      }
      setErrorMessage(msg);
    }
  };

  const filteredData = showHidden ? data : data.filter((e) => !e.hidden);

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                color="primary"
              />
            }
            label="Show Hidden"
          />
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAdd}
          >
            Add New
          </Button>
        </Stack>
      </Stack>
      <Paper variant="outlined">
        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          {filteredData.map((entity, index) => (
            <ListItem
              key={entity.id}
              divider={index < filteredData.length - 1}
              alignItems="flex-start"
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleEdit(entity)}
                >
                  <EditIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Avatar
                  src={logoUrl(entityType, entity)}
                  sx={{
                    bgcolor: entity.colorBg || "transparent",
                    color: entity.colorFg || "inherit",
                    border: "1px solid #ccc",
                    width: 40,
                    height: 40,
                    fontSize: "1rem",
                  }}
                >
                  {entity.name.slice(0, 2).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      component="span"
                      variant="subtitle1"
                      sx={{
                        fontWeight:
                          entity.style === "bold"
                            ? "bold"
                            : entity.style === "italic"
                              ? "normal"
                              : "normal",
                        fontStyle:
                          entity.style === "italic" ? "italic" : "normal",
                      }}
                    >
                      {entity.name}
                    </Typography>
                    {entity.hidden && (
                      <VisibilityOffIcon
                        fontSize="small"
                        color="action"
                        titleAccess="Hidden"
                      />
                    )}
                    {entity.priority !== undefined && entity.priority !== 0 && (
                      <Chip
                        label={`P${entity.priority}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Stack>
                }
                secondary={
                  entity.description ? (
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        maxWidth: "80%",
                      }}
                    >
                      {entity.description}
                    </Typography>
                  ) : null
                }
              />
            </ListItem>
          ))}
          {filteredData.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No items found"
                secondary={`Click "Add New" to create a ${title.toLowerCase().slice(0, -1)}.`}
              />
            </ListItem>
          )}
        </List>
      </Paper>
      {/* The page title is plural ("Categories") but the dialog wants the
          singular. Chopping a trailing "s" produced "Edit Categorie", so take
          the name the entity pickers already use for this kind. */}
      <EntityFormDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        initialData={selectedEntity}
        title={ENTITY_LABELS[entityType]}
        error={errorMessage}
        entityType={entityType}
      />
    </Box>
  );
};
